import React, { useState } from 'react';
import {
  Box,
  Popover,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Loader from '../Loader';

interface AppliedFiltersProps {
  appliedFilters: Record<string, string[]>;
  removeFilter: (fieldName: string, value: string) => void;
  clearAllFilters: () => void;
  isLoading: boolean;
  clearFieldFilter: (fieldName: string) => void;
}

const AppliedFilters: React.FC<AppliedFiltersProps> = ({
  appliedFilters,
  removeFilter,
  clearAllFilters,
  isLoading,
  clearFieldFilter,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openField, setOpenField] = useState<string | null>(null);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    field: string,
  ): void => {
    setAnchorEl(event.currentTarget);
    setOpenField(field);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setOpenField(null);
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      {isLoading ? (
        <Loader />
      ) : (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {Object.entries(appliedFilters).map(([fieldName, values]) =>
            values.length > 0 ? (
              <Box
                key={fieldName}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: 2,
                  mb: 2,
                  border: '1px solid #4D5E71',
                  borderRadius: '20px',
                  padding: '4px 12px',
                  paddingRight: '28px',
                  backgroundColor: 'white',
                  position: 'relative',
                }}
              >
                <Typography variant="body2" sx={{ mr: 1, color: '#4D5E71' }}>
                  {fieldName}:
                </Typography>
                <Typography variant="body2" sx={{ color: '#4D5E71', mr: 1 }}>
                  {values[0]}
                </Typography>
                {values.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={(event) => handleClick(event, fieldName)}
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: '#E0E0E0',
                      '&:hover': {
                        backgroundColor: '#C0C0C0',
                      },
                      borderRadius: '50%',
                      padding: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', color: '#4D5E71' }}
                    >
                      +{values.length - 1}
                    </Typography>
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => clearFieldFilter(fieldName)}
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    p: 0,
                    width: 16,
                    height: 16,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: '0.75rem', color: '#4D5E71' }} />
                </IconButton>
                <Popover
                  open={open && openField === fieldName}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      maxHeight: 200,
                      overflowY: 'auto',
                      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      mt: 1,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#ED4B52',
                        borderRadius: '8px',
                        transition: 'background-color 0.3s',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#C62828',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f5f5f5',
                      },
                    },
                  }}
                >
                  <Box>
                    <List sx={{ padding: 0 }}>
                      {values.map((value, index) => (
                        <ListItem
                          key={`${fieldName}-${value}-${index}`}
                          sx={{
                            padding: '8px 16px',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            borderBottom:
                              index < values.length - 1
                                ? '1px solid #e0e0e0'
                                : 'none',
                          }}
                        >
                          <ListItemText
                            primary={value}
                            sx={{
                              '& .MuiTypography-root': {
                                fontSize: '0.9rem',
                                color: '#4D5E71',
                              },
                            }}
                          />
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => removeFilter(fieldName, value)}
                            sx={{
                              color: '#888',
                              '&:hover': {
                                color: '#ED4B52',
                                backgroundColor: 'rgba(237, 75, 82, 0.1)',
                              },
                            }}
                          >
                            <HighlightOffIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Popover>
              </Box>
            ) : ''
          ) }
          {Object.keys(appliedFilters).length > 0 && (
      
            <Box sx={{ display: 'flex', ml: 'auto', mb:'10px', color:'gray' }}>        
            <p 
            style={{cursor:'pointer'}}            
              onClick={clearAllFilters}              
            >
              Clear filters
            </p>
          </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default AppliedFilters;
